import dayjs from 'dayjs';
import {LineChartIcon} from 'lucide-react';
import {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {CartesianGrid, Line, LineChart, XAxis, YAxis} from 'recharts';

import InfoState from '@/components/InfoState';
import {
  type ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart';
import type {HealthLogSeries} from '@/shared/residents/utils/healthLogUtils';
import {TIME_FORMATS} from '@/utils/timeFormats';
import {cn} from '@/utils/ui';

type ChartPoint = {
  recordedAt: Date;
  [seriesKey: string]: number | Date;
};

type HealthLogLineChartProps = {
  metricSeries: HealthLogSeries[];
  emptyStateDescription?: string;
  className?: string;
};

export function HealthLogLineChart({
  metricSeries,
  emptyStateDescription,
  className,
}: HealthLogLineChartProps) {
  const {t} = useTranslation();

  const mergedChartData = useMemo<ChartPoint[]>(() => {
    return metricSeries.reduce<ChartPoint[]>(
      (mergedPoints, seriesDefinition) => {
        return seriesDefinition.points.reduce<ChartPoint[]>(
          (currentPoints, seriesPoint) => {
            const existingPoint = currentPoints.find(
              point => point.recordedAt.getTime() === seriesPoint.x.getTime()
            );

            if (existingPoint) {
              return currentPoints.map(point =>
                point === existingPoint
                  ? {...point, [seriesDefinition.fieldName]: seriesPoint.y}
                  : point
              );
            }

            return [
              {
                recordedAt: seriesPoint.x,
                [seriesDefinition.fieldName]: seriesPoint.y,
              },
              ...currentPoints,
            ];
          },
          mergedPoints
        );
      },
      []
    );
  }, [metricSeries]);

  const seriesConfig = useMemo<ChartConfig>(() => {
    const palette = [
      'var(--chart-1)',
      'var(--chart-2)',
      'var(--chart-3)',
      'var(--chart-4)',
      'var(--chart-5)',
    ];

    return metricSeries.reduce<ChartConfig>(
      (config, seriesDefinition, index) => {
        config[seriesDefinition.fieldName] = {
          label: seriesDefinition.label,
          color: palette[index % palette.length],
        };
        return config;
      },
      {}
    );
  }, [metricSeries]);

  const [hiddenSeries, setHiddenSeries] = useState<string[]>([]);

  const seriesFieldNames = useMemo(
    () => metricSeries.map(s => s.fieldName).join(','),
    [metricSeries]
  );

  useEffect(() => {
    setHiddenSeries([]);
  }, [seriesFieldNames]);

  const handleLegendClick = (dataKey: string) => {
    if (metricSeries.length <= 1) return;
    setHiddenSeries(prev =>
      prev.includes(dataKey)
        ? prev.filter(key => key !== dataKey)
        : [...prev, dataKey]
    );
  };

  if (mergedChartData.length === 0) {
    return (
      <InfoState
        icon={<LineChartIcon size={50} className="text-mint-500" />}
        title={t('No chart data')}
        description={emptyStateDescription}
      />
    );
  }

  return (
    <ChartContainer
      config={seriesConfig}
      className={cn('h-full w-full', className)}
    >
      <LineChart accessibilityLayer data={mergedChartData}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />

        <ChartLegend
          wrapperStyle={{paddingTop: 50}}
          content={({payload}) => {
            if (!payload?.length) return null;
            const isToggleable = metricSeries.length > 1;
            return (
              <div className="flex items-center justify-center gap-4 pt-3">
                {payload
                  .filter(item => item.type !== 'none')
                  .map(item => {
                    const key = item.dataKey as string;
                    const isHidden = hiddenSeries.includes(key);
                    return (
                      <div
                        key={key}
                        className={cn(
                          'flex items-center gap-1.5 text-xs transition-opacity',
                          isToggleable && 'cursor-pointer select-none',
                          isHidden && 'opacity-40'
                        )}
                        onClick={() => handleLegendClick(key)}
                      >
                        <div
                          className="size-2 shrink-0 rounded-[2px]"
                          style={{backgroundColor: item.color}}
                        />
                        {item.value}
                      </div>
                    );
                  })}
              </div>
            );
          }}
        />

        <XAxis
          dataKey="recordedAt"
          angle={-45}
          textAnchor="end"
          dy={10}
          tickFormatter={value =>
            dayjs(value as Date).format(TIME_FORMATS.date)
          }
        />

        <YAxis domain={([dataMin, dataMax]) => [dataMin - 1, dataMax + 1]} />

        <ChartTooltip
          cursor={{strokeDasharray: '3 3'}}
          content={
            <ChartTooltipContent
              indicator="line"
              labelFormatter={(_, payload) => {
                return dayjs(
                  (payload.at(0)?.payload as Record<string, unknown>)
                    ?.recordedAt as Date
                ).format(TIME_FORMATS.dateTime);
              }}
            />
          }
        />

        {metricSeries.map(seriesDefinition => (
          <Line
            key={seriesDefinition.fieldName}
            type="monotone"
            dataKey={seriesDefinition.fieldName}
            name={seriesDefinition.label}
            stroke={`var(--color-${seriesDefinition.fieldName})`}
            strokeWidth={2}
            dot={{r: 2}}
            activeDot={{r: 4}}
            hide={hiddenSeries.includes(seriesDefinition.fieldName)}
          />
        ))}
      </LineChart>
    </ChartContainer>
  );
}
